/**
 * Legal document version strings.
 *
 * Bump these when publishing a new privacy policy or terms-of-service
 * version. The values are stamped into `consent_events.version` rows
 * captured at registration and activation, so changing them creates a
 * new consent surface that users will be re-prompted for on next login
 * (per docs/05-operations/data-management.md §9.2).
 *
 * Format: 'YYYY-MM-DD' — date the version was effective.
 *
 * IMPORTANT: When you bump a version here, also update the rendered
 * effective-date in the matching page component, and append the new
 * version to the version history table near the bottom of that page.
 */
export const PRIVACY_POLICY_VERSION = '2026-05-05-v1';
export const TERMS_OF_SERVICE_VERSION = '2026-05-05-v1';
export const AGE_ATTESTATION_VERSION = '2026-04-25';

/**
 * Consent type identifiers written to `consent_events.consent_type`.
 * Mirrors the spec list in docs/05-operations/data-management.md §9.1
 * with the addition of `age_18_plus` for the under-18 gate (SR-09) and
 * `visitor_data_lawful_basis` for the inviting-resident attestation.
 */
export const CONSENT_TYPES = {
  PRIVACY_POLICY: 'privacy_policy',
  TERMS_OF_SERVICE: 'terms_of_service',
  AGE_18_PLUS: 'age_18_plus',
  MARKETING_EMAIL: 'marketing_email',
  DATA_PROCESSING_AGREEMENT: 'data_processing_agreement',
  VISITOR_DATA_LAWFUL_BASIS: 'visitor_data_lawful_basis',
} as const;

export type ConsentType = (typeof CONSENT_TYPES)[keyof typeof CONSENT_TYPES];

/**
 * The three scopes the server checks on every authenticated request.
 * If any of these is missing or out-of-date the server returns
 * `X-Consent-Outdated` and the resident must re-accept.
 */
export type RequiredConsentScope = 'privacy_policy' | 'terms_of_service' | 'age_18_plus';

export const REQUIRED_CONSENT_SCOPES: readonly RequiredConsentScope[] = [
  'privacy_policy',
  'terms_of_service',
  'age_18_plus',
] as const;

/**
 * Map a required scope to the version string the user must accept now.
 */
export function expectedVersionFor(scope: RequiredConsentScope): string {
  switch (scope) {
    case 'privacy_policy':
      return PRIVACY_POLICY_VERSION;
    case 'terms_of_service':
      return TERMS_OF_SERVICE_VERSION;
    case 'age_18_plus':
      return AGE_ATTESTATION_VERSION;
  }
}

/**
 * Human-readable label for a scope (used in UI).
 */
export function labelFor(scope: RequiredConsentScope): string {
  switch (scope) {
    case 'privacy_policy':
      return 'Privacy Policy';
    case 'terms_of_service':
      return 'Terms of Service';
    case 'age_18_plus':
      return 'I am at least 18 years old';
  }
}
